import React, { useMemo, useState } from 'react';
import { ISummaryItemProps } from '../../../../../shared/Summary/SummaryItem/SummaryItem';
import {
  BoldSmallBodyText,
  Button,
  CreatableSelectField,
  FlexRow,
  Grid,
  ISelectOption,
  NavigationItem,
  SelectField,
  SmallBodyText,
  Spinner,
  TextField,
} from '../../../../../../ui-toolkit';
import { DisplayOrderGroupCategory, eOrderGroupCategory } from '../../../../../../ui-toolkit/enums/eOrderGroupCategory';
import { Show } from '../../../../../shared/Show';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit } from '@fortawesome/pro-regular-svg-icons';
import { Form, Formik } from 'formik';
import { orderGroupValidator } from '../../../../../../validation/order/OrderGroupValidator';
import { StyledSummaryItem } from '../../../../../shared/Summary/SummaryItem/SummaryItem.styles';
import { IUpdateOrderPatchRequest } from '../../../../../../ui-toolkit/orders/interfaces/IUpdateOrderPatchRequest';
import {
  useCreateMobilityGroupMutation,
  useListTransactionTypesQuery,
  useListMobilityGroupsQuery,
} from '../../../../../../API/mobilityGroups/MobilityGroupApi';
import { buildMobilityGroupsOptions } from '../../../../../orderCreation/GroupInformationForm';
import { checkForMutationSuccess } from '../../../../../../API/checkForMutationSuccess';
import { SelectOption } from '../../../../../../tailwindComponents/selectTW/interfaces';
import { useShouldSummaryItemRender } from '../../../../../shared/Summary/useShouldSummaryItemRender';
import { eTransactionCategory } from '../../../../../../ui-toolkit/enums/eTransactionType';
import { IOrderDetailsData } from '../../../../../../ui-toolkit/orders/interfaces/IOrderDetailsData';
import { convertUndefinedToNull } from '../../../../../../ui-toolkit/utilities/convertUndefinedToNull';

interface IOrderTypeSummaryItemContainerProps extends ISummaryItemProps {
  order: IOrderDetailsData;
  onUpdate: (patchData: Partial<IUpdateOrderPatchRequest>) => Promise<void>;
}

export const getOrderGroupOptionsForTransactionCategory = (
  transactionCategory: eTransactionCategory
): ISelectOption[] => {
  return Object.keys(eOrderGroupCategory)
    .filter((groupType) => {
      if (transactionCategory === eTransactionCategory.Refinance) {
        return groupType === 'Bulk';
      }
      return true;
    })
    .map((group) => ({
      label: group,
      value: eOrderGroupCategory[group],
    }));
};

export const getGroupIdBasedOnCategory = (order: IOrderDetailsData): string | undefined | null => {
  const groupCategory = order.transaction.order_group_category;
  switch (groupCategory) {
    case eOrderGroupCategory.Bulk:
      return order.transaction.order_group_id;
    case eOrderGroupCategory.Mobility:
      return order.transaction.mobility_group?.external_id;
    default:
      return null;
  }
};

export const OrderTypeSummaryItemContainer: React.FC<IOrderTypeSummaryItemContainerProps> = ({
  label,
  order,
  onUpdate,
  tab,
}) => {
  const {
    data: mobilityGroups,
    isSuccess: mobilityGroupsIsSuccess,
    isLoading: mobilityGroupsIsLoading,
  } = useListMobilityGroupsQuery(order.client.id, {
    skip: false,
  });

  const [createGroupApi, { reset, isLoading: isGroupCreateLoading }] = useCreateMobilityGroupMutation();
  const { data: transactionTypes = [] } = useListTransactionTypesQuery();

  const transactionTypeOptions: SelectOption[] = transactionTypes.map((tType) => ({
    label: tType.label,
    value: tType.id,
  }));

  const onGroupSelected = async (
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void,
    option?: {
      label: string;
      value: string;
      __isNew__?: boolean;
    } | null
  ) => {
    if (option?.__isNew__) {
      const result = await createGroupApi({
        externalId: option.value,
        clientId: order.client.id,
      });
      if (checkForMutationSuccess(result, 'creating group')) {
        if ('data' in result) {
          setFieldValue('mobilityGroupId', result.data.id);
          reset();
        }
      }
    }
  };

  const mobilityGroupsOptions: ISelectOption[] = useMemo(() => {
    return buildMobilityGroupsOptions(mobilityGroups, mobilityGroupsIsSuccess, mobilityGroupsIsLoading, true);
  }, [mobilityGroups, mobilityGroupsIsSuccess, mobilityGroupsIsLoading]);

  const [showEditOrderGroup, setShowEditOrderGroup] = useState(false);
  const orderHasGroup = order.transaction.order_group_category !== null;
  const groupId = getGroupIdBasedOnCategory(order);
  const shouldRender = useShouldSummaryItemRender(label, tab);
  if (!shouldRender) {
    return null;
  }
  return (
    <StyledSummaryItem>
      <FlexRow justifyContent={'space-between'}>
        <BoldSmallBodyText>{label}</BoldSmallBodyText>
        <NavigationItem text={`Edit ${label}`} onClick={() => setShowEditOrderGroup(true)}>
          <FontAwesomeIcon icon={faEdit} data-test-id={'order-type-edit-button'} />
        </NavigationItem>
      </FlexRow>
      {showEditOrderGroup ? (
        <Formik
          initialValues={{
            groupCategory: order.transaction.order_group_category,
            bulkGroupId: order.transaction.order_group_id,
            mobilityGroupId: order.transaction.mobility_group?.id,
            transactionType: order.transaction.mobility_transaction_type,
          }}
          validateOnMount={true}
          validationSchema={orderGroupValidator}
          enableReinitialize={true}
          onSubmit={async (values) => {
            const groupId =
              values.groupCategory === eOrderGroupCategory.Mobility ? values.mobilityGroupId : values.bulkGroupId;
            const updateData: Partial<IUpdateOrderPatchRequest> = {
              groupCategory: convertUndefinedToNull(values.groupCategory),
              groupId: groupId,
              transactionType: values.transactionType,
            };
            await onUpdate(updateData);
            setShowEditOrderGroup(false);
          }}
        >
          {({ isSubmitting, values, setFieldValue }) => (
            <Form>
              <Grid>
                <Show when={!isSubmitting} fallback={<Spinner />}>
                  <SelectField
                    name={'groupCategory'}
                    label={'Order Type'}
                    isClearable
                    options={getOrderGroupOptionsForTransactionCategory(order.transaction.transaction_category)}
                  />
                  <Show when={values.groupCategory === eOrderGroupCategory.Mobility}>
                    <CreatableSelectField
                      isLoading={mobilityGroupsIsLoading || isGroupCreateLoading}
                      name={'mobilityGroupId'}
                      label={'Select a Group'}
                      options={mobilityGroupsOptions}
                      onChange={async (newValue) => {
                        await onGroupSelected(setFieldValue, newValue);
                      }}
                    />
                    <SelectField
                      name={'transactionType'}
                      label={'Mobility Transaction Type'}
                      options={transactionTypeOptions}
                    />
                  </Show>
                  <Show when={values.groupCategory === eOrderGroupCategory.Bulk}>
                    <TextField name={'bulkGroupId'} label={'Group Id'} />
                  </Show>
                  <Button intent="primary" isInline type="submit" data-test-id={`order-type-update-button`}>
                    Update
                  </Button>
                </Show>
              </Grid>
            </Form>
          )}
        </Formik>
      ) : !orderHasGroup || !groupId ? (
        <SmallBodyText intent={`faded`}>-</SmallBodyText>
      ) : (
        <SmallBodyText>
          {DisplayOrderGroupCategory[order.transaction.order_group_category!]} - {groupId}
        </SmallBodyText>
      )}
    </StyledSummaryItem>
  );
};
